import { useCallback, useEffect, useState } from 'react';
import { PlatformRating, Review } from '../type';
import { reviewsCacheService } from '../services/ReviewsCacheService';
import { NetworkLoadingState } from '../types/NetworkTypes';
import { invalidateUnreadReviewsCache } from '../hooks/useUnreadReviews';

export const useSocialNetworkData = (
    userMyId: string,
    refreshTrigger?: number,
    statusOnly: boolean = false,
) => {
    const [reviews, setReviews] = useState<Review[]>([]);
    const [loadingState, setLoadingState] = useState<NetworkLoadingState>({
        gmb: true,
        facebook: true,
        opinionSystem: true,
    });
    const [platformRatings, setPlatformRatings] = useState<PlatformRating[]>([]);

    useEffect(() => {
        if (!userMyId) return;

        const unsubscribe = reviewsCacheService.subscribe(userMyId, () => {
            const cacheInfo = reviewsCacheService.getCacheInfo(userMyId);
            if (cacheInfo) {
                setLoadingState(cacheInfo.loadingState);
                setPlatformRatings(cacheInfo.platformRatings);
                setReviews(cacheInfo.reviews);
            }
        });

        return unsubscribe;
    }, [userMyId]);

    useEffect(() => {
        const updateReviews = async () => {
            if (!userMyId) {
                setLoadingState({ gmb: false, facebook: false, opinionSystem: false });
                return;
            }

            if (statusOnly) {
                setLoadingState({ gmb: false, facebook: false, opinionSystem: false });
                setReviews([]);
                setPlatformRatings([]);
                return;
            }

            try {
                setLoadingState({ gmb: true, facebook: true, opinionSystem: true });

                const {
                    reviews: cachedReviews,
                    platformRatings: cachedRatings,
                    loadingState: newLoadingState,
                } = await reviewsCacheService.getReviews(
                    userMyId,
                    refreshTrigger ? refreshTrigger > 1 : false,
                );

                setLoadingState(prev => {
                    const hasUpdates = Object.values(prev).some(state => state === false);
                    return hasUpdates ? prev : newLoadingState;
                });

                if (cachedReviews.length > 0) {
                    setReviews(prev => (prev.length > 0 ? prev : cachedReviews));
                }
                if (cachedRatings.length > 0) {
                    setPlatformRatings(prev => (prev.length > 0 ? prev : cachedRatings));
                }

                if (cachedReviews.length > 0) {
                    invalidateUnreadReviewsCache(userMyId);
                }
            } catch (error) {
                console.error('Error retrieving reviews:', error);
                setLoadingState({ gmb: false, facebook: false, opinionSystem: false });
            }
        };

        updateReviews();
    }, [userMyId, refreshTrigger, statusOnly]);

    const handleStatusUpdate = useCallback(
        async (reviewsToUpdate: Review[]) => {
            try {
                await reviewsCacheService.updateReviewStatus(userMyId, reviewsToUpdate);

                const { reviews: updatedReviews } = await reviewsCacheService.getReviews(userMyId);
                setReviews(updatedReviews);
            } catch (error) {
                console.error('Error updating review status:', error);
            }
        },
        [userMyId],
    );

    return {
        reviews,
        loadingState,
        platformRatings,
        handleStatusUpdate,
    };
};
